import React, { useMemo, useState } from 'react';
import cx from 'classnames';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { useApplicationState } from '@apollo/core';
import { useRacingState } from '@apollo/core/src/state/racing/racing';
import Dropdown from '../../../src/app/shared/components/Dropdown/Dropdown';
import { AnimatedDropdown } from '../../../src/app/features/Animations/AnimatedDropdown';
import RaceLink from '../../../src/app/shared/components/Racing/RaceLink';

import Label from '../../../src/app/shared/components/I18n/Label';
import RaceMarketSubTabs from '../../../src/app/features/Racing/RacePage/RaceMarketSubTabs';
import RaceMarketTabs from '../../../src/app/features/Racing/RacePage/RaceMarketTabs';
import { RacingIcon } from '../../../src/app/shared/components/Racing/RacingIcon';
import useTranslate from '../../../src/app/shared/components/I18n/Interpreter';
import RaceCountdown from '../../../src/app/shared/components/Racing/RaceCountdown/RaceCountdown';
import { getRaceCompleteText } from '../../../src/app/core/utils';
import RaceNumberList from '../../../src/app/features/Racing/RacePage/RaceNumberList';
import { RacingStatus } from '../../../src/app/shared/components/Racing/RacingStatus';
import NextToJumpBar from '../../../src/app/shared/components/Header/NextToJumpBar/NextToJumpBar';
import { RACE_STATUS } from '../../../src/app/core/constants';

const RacePageHead = ({
  displaySettings,
  setDisplaySettings,
  marketTabs,
  marketSubTabs,
  availableMultilegs = [],
  activeMultiLegs = [],
}) => {
  const t = useTranslate();
  const history = useHistory();
  RacePageHead.displayName = 'FavBet.RacePageHead';

  const [racingState] = useRacingState();
  const { layout, authenticated } = useApplicationState();

  const { activeMarketTypeId, meetings, activeRace, activeMeet, marketTab } = racingState;

  const [isShowRaceList, setIsShowRaceList] = useState(false);

  const meetingsList = useMemo(() => {
    if (activeMeet == null) {
      return [];
    }
    return (meetings || []).filter(
      ({ type, country, date }) => type === activeMeet.type
        && date === activeMeet.date
        && (activeMeet.country === 'AUS' ? country === 'AUS' : country !== 'AUS'),
    );
  }, [meetings, activeMeet]);

  return (
    <>
      {layout.mobileDevice && <NextToJumpBar />}
      <div className='race-container'>
        <div className='race-nav__container'>
          <div className='race-page__header'>
            <div className='race__info'>
              {activeRace ? (
                <RacingIcon id={activeRace.raceType?.id} name={activeRace.raceType?.typeName} />
              ) : null}
              <Dropdown onExpanded={setIsShowRaceList} className='race-list-dropdown'>
                <div className='race-name' title={activeMeet.venue.venueName}>
                  <h1 className='size-24'>{activeMeet.venue.venueName}</h1>
                  <div className='race-desc'>
                    {activeRace?.number && (
                      <span className='race-desc__number'>{`• ${layout.mobileDevice ? 'R' : `${t('race')} `}${activeRace.number} • `}</span>
                    )}
                    {activeRace?.distance && (
                      <span className='race-desc__distance'>
                        <span>
                          {activeRace.distance}
                          m
                        </span>
                      </span>
                    )}
                  </div>
                </div>

                <AnimatedDropdown
                  isExpanded={isShowRaceList}
                  className='race-list race-list__dropdown'
                  endOverflow='auto'
                >
                  {meetingsList.map((meeting) => (
                    <div
                      key={meeting.meetingId}
                      className={cx([
                        'race-list-item',
                        meeting.meetingId === activeMeet.meetingId && ' active',
                      ])}
                    >
                      <RaceLink
                        raceDate={meeting.date}
                        raceType={meeting.raceType.typeName}
                        raceCountry={meeting.country}
                        raceVenue={meeting.venueName}
                        raceNumber={meeting.races[0]?.number}
                      >
                        {meeting.venueName}
                      </RaceLink>
                    </div>
                  ))}
                </AnimatedDropdown>
              </Dropdown>

              {activeRace && (
                <div className='race-track'>
                  {activeRace?.going && (
                    <span>
                      <span>{activeRace.going}</span>
                    </span>
                  )}
                </div>
              )}

              {activeRace && (
                <div className='race-countdown-wrapper' title={`id:${activeRace.raceId}`}>
                  <div className='race-countdown-status'>
                    {activeRace.status === RACE_STATUS.OPEN ? (
                      <RaceCountdown
                        key={`${activeRace.raceId}-${activeRace.status}-${activeRace.result}`}
                        date={new Date(activeRace.startTime)}
                        completeText={getRaceCompleteText(activeRace)}
                      />
                    ) : null}

                    <RacingStatus status={activeRace.status} />
                  </div>
                  {activeRace?.startTime && (
                    <div className='race__metadata race__metadata__time'>
                      {moment(activeRace.startTime).format('DD MMM hh:mmA')}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className='race__metadata'>
              {layout.mobileDevice && activeRace?.distance && (
                <div className='race__metadata__item'>
                  <span className='race__metadata__value'>
                    {activeRace.distance}
                    m
                  </span>
                </div>
              )}
              {activeRace?.title && (
                <div className='race__metadata__item'>
                  {!layout.mobileDevice && (
                    <span className='race__metadata__name'>
                      {t('Title')}
                      :
                    </span>
                  )}
                  <span className='race__metadata__value'>{activeRace?.title}</span>
                </div>
              )}
              {activeRace?.raceClass && (
                <div className='race__metadata__item'>
                  <span className='race__metadata__name'>
                    {t('Race Class')}
                    :
                  </span>
                  <span className='race__metadata__value'>
                    {activeRace.raceClass.replace(/,[s]*/g, ', ')}
                  </span>
                </div>
              )}
              {activeRace?.weather && (
                <div className='race__metadata__item'>
                  <span className='race__metadata__name'>
                    {t('Weather')}
                    :
                  </span>
                  <span className='race__metadata__value'>{activeRace.weather}</span>
                </div>
              )}
            </div>
            <div className='race-numbers '>
              <div className='race__number__container'>
                <RaceNumberList
                  marketTab={marketTab}
                  activeMultiLegs={activeMultiLegs}
                  availableMultilegs={availableMultilegs}
                />
              </div>
            </div>
          </div>
        </div>
        <RaceMarketTabs
          activeTab={marketTab}
          marketTabs={marketTabs}
          marketSubTabs={marketSubTabs}
        />
      </div>
      <div className='race-settings-wrapper'>
        {marketSubTabs[marketTab]?.length > 1 ? (
          <RaceMarketSubTabs
            activeTab={marketTab}
            activeMarketTypeId={activeMarketTypeId}
            marketSubTabs={marketSubTabs}
          />
        ) : null}

        <div className='display-settings tournament__header'>
          <div className='display-settings__item'>
            <div
              className='settings--checkbox'
              onClick={() => {
                if (!authenticated) {
                  history.push('?cmd=signin');
                }
              }}
            >
              <input
                type='checkbox'
                id='settings-checkbox-1'
                checked={authenticated && displaySettings.isSpeedMap}
                onChange={() => setDisplaySettings({
                  ...displaySettings,
                  isSpeedMap: !authenticated || !displaySettings.isSpeedMap,
                })}
              />
              <label htmlFor='settings-checkbox-1' className='filter-label'>
                <span className='filter-icon speedMap' />
                <Label message='race speed map' />
              </label>
            </div>
          </div>

          <div className='display-settings__item'>
            <div
              className='settings--checkbox'
              onClick={() => {
                if (!authenticated) {
                  history.push('?cmd=signin');
                }
              }}
            >
              <input
                type='checkbox'
                id='settings-checkbox-2'
                checked={authenticated && displaySettings.isForm}
                onChange={() => setDisplaySettings({
                  ...displaySettings,
                  isForm: !authenticated || !displaySettings.isForm,
                })}
              />
              <label htmlFor='settings-checkbox-2' className='filter-label'>
                <span className='filter-icon form' />

                <Label message='race form' />
              </label>
            </div>
          </div>

          <div className='display-settings__item'>
            <div className='settings--checkbox'>
              <input
                type='checkbox'
                id='settings-checkbox-3'
                checked={displaySettings.isFlucs}
                onChange={() => setDisplaySettings({
                  ...displaySettings,
                  isFlucs: !displaySettings.isFlucs,
                })}
              />
              <label htmlFor='settings-checkbox-3' className='filter-label'>
                <span className='filter-icon flucs' />
                <Label message='flucs' />
              </label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RacePageHead;
